import type { ComparisonPage } from '../comparisons';

const CHECKED = '2026-10-06';
/** "6 October 2026": the prose date comes from the same constant as the sources and lastReviewed. */
const CHECKED_LONG = new Date(`${CHECKED}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

/** /alternatives/jobber. Draft 2026-10-06. Not approved for publishing; re-verify every Jobber fact on publish day. */
const page: ComparisonPage = {
  slug: 'alternatives-jobber',
  path: '/alternatives/jobber',
  primaryKeyword: 'jobber alternative',
  secondaryKeywords: ['jobber alternative for small service business', 'apps like jobber', 'jobber alternative with e-signature'],
  title: 'Jobber Alternative for Quote-First Businesses | Scopie', // 54
  metaDescription:
    'Looking for a Jobber alternative? Scopie is quote-first: line-item quotes signed online, then jobs, crews and invoices. See what it does and does not do.', // 153
  navLabel: 'Jobber alternative',
  h1: 'A Jobber alternative built around the quote',
  answerFirst:
    'Scopie is a quote-first alternative to Jobber for service businesses that win work with quotes. Customers sign line-item quotes online, then you schedule the job with its crew and equipment and invoice it. Scopie is newer and does less: no QuickBooks sync, no SMS and no route optimization.',
  lastReviewed: CHECKED,
  sections: [
    {
      id: 'who-for',
      h2: 'Who should consider Scopie instead of Jobber',
      body: [
        'Scopie fits businesses that win most of their work with a quote: pressure washing, deep cleans and move-outs, landscaping and hardscape, painting, fencing, tree work, flooring and similar project-style jobs. If your day starts with a site visit and ends with a signed quote, Scopie is built around that step.',
        'It also suits owners who want their crew kept away from prices. In Scopie, crew members see their own schedule in My day, while quotes, prices and invoices stay with the owner and the office.',
        'Teams that work in Spanish can use Scopie in Spanish, and each client can have their own language, English, Spanish or Brazilian Portuguese.',
      ],
    },
    {
      id: 'who-stay',
      h2: 'Who should stay with Jobber',
      body: [
        'Jobber does more than Scopie today. Stay with Jobber if you need to collect card or bank payments online now, sync with QuickBooks Online, text customers from the app, offer online booking, or automate quote and invoice follow-ups. Jobber lists all of these on its pricing page.',
        'Route-based recurring work, such as weekly mowing rounds, is also a better fit for a tool with recurring visits and billing, which Scopie does not have yet.',
      ],
    },
    {
      id: 'pricing',
      h2: 'Pricing, compared for the same team size',
      body: [
        'Jobber prices by plan and team size. On its pricing page, checked on ' + CHECKED_LONG + ', Jobber listed Core for one user at 49 dollars a month, or 29 dollars a month billed annually; Connect at 139 dollars a month for one user and 199 dollars a month for 5 users; and Grow at 199 dollars a month for one user and 299 dollars a month for 5 users. Annual billing was lower. Prices change, so check Jobber\'s pricing page for current figures.',
        'Scopie has three plans by team size: Solo for one user, Crew for up to 5 users and Team for up to 14 users, and every plan has every feature. Current Scopie prices are on the pricing page.',
      ],
    },
    {
      id: 'moving',
      h2: 'Moving from Jobber',
      body: [
        'Start with the clients you are quoting this month. Add them to Scopie, set up your usual line items and terms, and send your next quotes from Scopie while you finish open jobs in Jobber. Keep Jobber until your open invoices are paid.',
      ],
    },
  ],
  table: {
    caption: `Scopie and Jobber, side by side. Jobber facts from getjobber.com/pricing, checked ${CHECKED}.`,
    columns: ['', 'Scopie', 'Jobber'],
    rows: [
      { label: 'Free trial', cells: [{ text: '' }, { text: '14 days, no credit card' }, { text: 'Free trial, no credit card required', sources: ['jobber-pricing'] }] },
      { label: 'Plans', cells: [{ text: '' }, { text: 'Solo (1 user), Crew (up to 5), Team (up to 14); every plan has every feature' }, { text: 'Core (1 user), Connect and Grow (1, 5, 10 or 15 users); features differ by plan', sources: ['jobber-pricing'] }] },
      { label: 'Quotes signed online', cells: [{ text: '' }, { text: 'Yes, with a signature' }, { text: 'Sends professional quotes (Core and up)', sources: ['jobber-pricing'] }] },
      { label: 'Invoices', cells: [{ text: '' }, { text: 'Yes' }, { text: 'Yes (Core and up)', sources: ['jobber-pricing'] }] },
      { label: 'Online card and bank payments', cells: [{ text: '' }, { text: 'Yes, through your own Stripe account' }, { text: 'Receive online payments (Core and up)', sources: ['jobber-pricing'] }] },
      { label: 'Scheduling with crew and equipment', cells: [{ text: '' }, { text: 'Yes' }, { text: 'Book and schedule jobs (Core and up)', sources: ['jobber-pricing'] }] },
      { label: 'Optional line items on quotes', cells: [{ text: '' }, { text: 'Yes, customers tick the extras they want' }, { text: 'Grow plan', sources: ['jobber-pricing'] }] },
      { label: 'Automated quote and invoice follow-ups', cells: [{ text: '' }, { text: 'No' }, { text: 'Connect plan and up', sources: ['jobber-pricing'] }] },
      { label: 'QuickBooks Online sync', cells: [{ text: '' }, { text: 'No' }, { text: 'Connect plan and up', sources: ['jobber-pricing'] }] },
      { label: 'Two-way SMS', cells: [{ text: '' }, { text: 'No' }, { text: 'Grow plan', sources: ['jobber-pricing'] }] },
      { label: 'Online booking', cells: [{ text: '' }, { text: 'No' }, { text: 'Book jobs online (Core and up)', sources: ['jobber-pricing'] }] },
    ],
  },
  faq: [
    {
      q: 'Is Scopie cheaper than Jobber?',
      a: 'Compare the plan you need for the same number of users. Jobber lists its prices by plan and team size on its pricing page, and Scopie lists its prices on its own pricing page. Scopie has one price per team size with every feature included, while Jobber adds features as you move up from Core to Connect to Grow.',
    },
    {
      q: 'Can customers pay online in Scopie?',
      a: 'Yes. Once you connect your own Stripe account, your customers can pay deposits and invoices by card or bank transfer, and the money goes to your Stripe account. Until you connect it, quotes and invoices are paid by cash or check and you record the payment yourself.',
    },
    {
      q: 'Does Scopie sync with QuickBooks?',
      a: 'No. Scopie does not sync with QuickBooks or other accounting software. Jobber lists QuickBooks Online sync on its Connect and Grow plans.',
    },
    {
      q: 'Does Scopie have route optimization or SMS?',
      a: 'No. Scopie does not plan routes or send text messages. If those are central to your business, Jobber or another field service tool will fit better today.',
    },
    {
      q: 'Can I try Scopie without a credit card?',
      a: 'Yes. Scopie has a 14-day free trial with every feature and no credit card.',
    },
  ],
  sources: [{ id: 'jobber-pricing', url: 'https://www.getjobber.com/pricing/', checked: CHECKED, how: 'rendered page in a headless browser; team-size selector read for 1, 2-5, 6-10 and 11-15 people' }],
  disclaimer:
    'Jobber is a trademark of its owner. Scopie is not affiliated with Jobber. Jobber facts were taken from Jobber\'s public pricing page on the date shown and may have changed since.',
  internalLinks: [
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for', anchor: 'Industries Scopie is built for', status: 'live' },
    { href: '/for/pressure-washing', anchor: 'Scopie for pressure washing', status: 'live' },
    { href: '/for/landscaping', anchor: 'Scopie for landscaping', status: 'live' },
    { href: '/for/painting', anchor: 'Scopie for painting contractors', status: 'live' },
    { href: '/compare/jobber-vs-housecall-pro', anchor: 'Jobber vs Housecall Pro, compared', status: 'live' },
  ],
  cta: {
    heading: 'Try Scopie on your next quote',
    text: 'Set up your line items and terms, then share your next quote as a link.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [
    'Re-open https://www.getjobber.com/pricing/ on publish day: plan names, the 1/5-user prices quoted in the Pricing section, and the plan for each feature row. Update lastReviewed.',
    'The prices are written in words ("49 dollars") because the site test forbids "$" figures outside example quotes; web-f1 may prefer a sources-table exception for comparison pages instead.',
    'Scopie "follow-ups: No": confirm (follow-up emails are not switched on yet).',
    'Scopie "Online card and bank payments: Yes" and "Optional line items: Yes": both are live (online payments need the business to connect Stripe; optional lines are ticked by the customer on the quote page). Re-check if either changes.',
    'Moving section assumes clients are added by hand (no import claimed). If CSV import exists, say so.',
    'Signups are allowlist-only: the CTA needs the same decision as the rest of the site.',
    'Owner/legal review before publishing (nominative use of the Jobber name, Scopie LLC as publisher).',
  ],
};

export default page;
